/*
 * Copy for the merge dialog: field names, values and what moves.
 */
import type { MergePreview } from "@/api/types/clientDirectory";
import type { Address } from "@/api/types/foreignNational";

import { formatAddressLine } from "../../lib/format";

const SPECIAL: Record<string, string> = {
  a_number: "A-Number",
  ssn: "SSN",
  uscis_account_number: "USCIS online account number",
  ead_expiration: "EAD expiration",
  ead_number: "EAD number",
  i94_number: "I-94 number",
  h1b_max_out_date: "H-1B max-out date",
  birthday: "Date of birth",
  notes_md: "Notes"
};

export const fieldLabel = (field: string): string => {
  if (SPECIAL[field]) return SPECIAL[field];
  const words = field.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const isAddress = (v: unknown): v is Address =>
  typeof v === "object" &&
  v !== null &&
  !Array.isArray(v) &&
  ("street" in v || "city" in v || "country" in v);

export const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map(formatValue).join(", ");
  if (isAddress(value)) return formatAddressLine(value) || "—";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [y, m, d] = value.slice(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const COUNT_LABELS: Record<string, [string, string]> = {
  individuals: ["case", "cases"],
  outgoing_intakes: ["request", "requests"],
  documents: ["document", "documents"],
  address_history: ["address", "addresses"],
  employment_history: ["job", "jobs"],
  education_history: ["degree", "degrees"],
  immigration_status_history: ["status record", "status records"],
  immigration_entry_exit_history: ["trip", "trips"],
  passports: ["passport", "passports"],
  dependents: ["dependent", "dependents"],
  reminders: ["reminder", "reminders"],
  case_agent_thread: ["assistant thread", "assistant threads"],
  extend_uscis_filled_forms: ["filled form", "filled forms"]
};

/** "2 cases, 5 documents and 3 addresses", in a fixed order; empty kinds skipped. */
export const movesSummary = (counts: MergePreview["counts"]): string => {
  const parts = Object.entries(COUNT_LABELS)
    .map(([key, [one, many]]) => {
      const n = counts[key] ?? 0;
      return n > 0 ? `${n} ${n === 1 ? one : many}` : null;
    })
    .filter((p): p is string => p !== null);
  if (parts.length === 0) return "No records to move";
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
};
